import Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import {
  CLIENT_MESSAGES,
  SERVER_MESSAGES,
  type ServerChatMessage,
  type ServerGameLogMessage,
  type ServerHandUpdateMessage,
  type ServerPlayEventMessage,
  type ServerPassEventMessage,
  type ServerRoomStateMessage,
} from '@dalmuti/shared';
import { JOKER_RANK } from '@dalmuti/shared';
import { Board } from '../ui/Board.js';
import { CARD_HEIGHT, CARD_WIDTH, CardSprite } from '../ui/CardSprite.js';
import { SeatDomBadge } from '../ui/SeatDomBadge.js';
import { phaseLabel } from '../ui/theme.js';
import { resetOverlay } from '../ui/overlay.js';
import { transitionTo } from '../ui/transition.js';
import { sfx, setMuted, isMuted } from '../ui/sound.js';

interface HandCard {
  id: number;
  rank: number;
}

interface SeatStateRow {
  seat: number;
  sessionId: string;
  displayName: string;
  handCount: number;
  isBot: boolean;
  title: string;
  hasPassed: boolean;
  pendingTaxCount: number;
  pendingTaxTo: number;
  lastRoundPlacement: number;
  turnOrder: number;
}

interface TrickStateView {
  leadRank: number;
  count: number;
  jokersUsed: number;
  lastPlayerSeat: number;
  isEmpty: boolean;
}

interface RoomStateView {
  phase: string;
  currentSeat: number;
  revolutionActive: boolean;
  revolutionSeat: number;
  hostSessionId: string;
  seatOrder: SeatStateRow[];
  trick: TrickStateView;
  spectators: { sessionId: string; displayName: string }[];
  canSkip: boolean;
}

interface FloatingLine {
  el: HTMLDivElement;
  expiresAt: number;
}

export class GameScene extends Phaser.Scene {
  private room!: Room;
  private board!: Board;
  private hand: HandCard[] = [];
  private selectedIds = new Set<number>();
  private mySeat = -1;
  private seatBadges = new Map<number, SeatDomBadge>();
  private trickLayer!: Phaser.GameObjects.Container;
  private handLayer!: Phaser.GameObjects.Container;
  private cardSprites = new Map<number, CardSprite>();
  private phaseBanner!: Phaser.GameObjects.Text;
  private statusBanner!: Phaser.GameObjects.Text;
  private revBanner!: Phaser.GameObjects.Text;
  private actionPanel: HTMLDivElement | null = null;
  private chatFeed: HTMLDivElement | null = null;
  private chatInputBar: HTMLDivElement | null = null;
  private gameLogPanel: HTMLDivElement | null = null;
  private statusBadge: HTMLDivElement | null = null;
  private trickPanel: HTMLDivElement | null = null;
  private myCardPanel: HTMLDivElement | null = null;
  private resultBanner: HTMLDivElement | null = null;
  private nextGameButton: HTMLButtonElement | null = null;
  private skipButton: HTMLButtonElement | null = null;
  private fullscreenButton: HTMLButtonElement | null = null;
  private fsChangeHandler: (() => void) | null = null;
  private orientationHint: HTMLDivElement | null = null;
  private lastShownRoundEnd = -1;
  private playButton: HTMLButtonElement | null = null;
  private passButton: HTMLButtonElement | null = null;
  private revolutionButton: HTMLButtonElement | null = null;
  private declineRevolutionButton: HTMLButtonElement | null = null;
  private taxGiftButton: HTMLButtonElement | null = null;
  private passWrap: HTMLDivElement | null = null;
  private handPanelRightX = 0;
  // Global responsive scale (1 on desktop, smaller on phones). Drives card,
  // seat-badge and HUD sizing so the table fits small landscape screens.
  private uiScale = 1;
  // The local hand is rendered a bit larger than the rest (it's what the
  // player reads constantly), capped at 1 so desktop is unchanged.
  private handScale = 1;
  // Seat badges shrink less than the rest so opponents stay readable.
  private seatScale = 1;
  private spaceKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private dragStartY: number | null = null;
  private dragStartCardId: number | null = null;
  private currentTrick: { rank: number; count: number; jokers: number } | null = null;
  private revolutionActive = false;
  private roomState: ServerRoomStateMessage | null = null;
  private wasMyTurn = false;
  private lastSeenPhase = '';
  private floatingChatLines: FloatingLine[] = [];
  private resizeHandler = (): void => this.layout();
  private fadeTickerHandle = -1;
  private cleanedUp = false;
  private finishedReturnScheduled = false;

  constructor() {
    super('Game');
  }

  init(data: { room: Room }): void {
    this.room = data.room;
  }

  preload(): void {
    for (let i = 1; i <= 12; i++) {
      if (!this.textures.exists(`card-${i}`)) {
        this.load.image(`card-${i}`, `/cards/rank_${i}.png`);
      }
    }
    if (!this.textures.exists('card-joker')) {
      this.load.image('card-joker', '/cards/joker.png');
    }
    if (!this.textures.exists('card-back')) {
      this.load.image('card-back', '/cards/back.png');
    }
  }

  create(): void {
    resetOverlay();
    this.cleanedUp = false;
    this.finishedReturnScheduled = false;
    this.seatBadges = new Map();
    this.currentTrick = null;
    this.revolutionActive = false;
    this.roomState = null;
    this.wasMyTurn = false;
    this.lastSeenPhase = '';
    this.cardSprites = new Map();
    this.selectedIds = new Set();
    this.floatingChatLines = [];
    this.hand = [];
    this.mySeat = -1;
    this.board = new Board(this);
    this.handLayer = this.add.container(0, 0);
    this.handLayer.setDepth(10);
    this.trickLayer = this.add.container(0, 0);
    this.trickLayer.setDepth(35);

    // Phaser banners stay around as fallback but are visually hidden now
    // — the DOM status badge + trick panel take their place.
    this.phaseBanner = this.add
      .text(0, 0, '', { fontSize: '1px', color: '#00000000' })
      .setOrigin(0.5, 0)
      .setVisible(false);
    this.statusBanner = this.add
      .text(0, 0, '', { fontSize: '1px', color: '#00000000' })
      .setOrigin(0.5, 0)
      .setVisible(false);
    this.revBanner = this.add
      .text(0, 0, '', {
        fontFamily: 'Georgia, serif',
        fontSize: '36px',
        color: '#ff7a7a',
        fontStyle: 'bold italic',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.phaseBanner.setDepth(60);
    this.statusBanner.setDepth(60);
    this.revBanner.setDepth(70);

    this.mountStatusBadge();
    this.mountTrickPanel();
    this.mountChatFeed();
    this.mountChatInput();
    this.mountActions();
    this.mountNextGameButton();
    this.mountSkipButton();
    this.mountOrientationHint();
    this.setupHandPointerInput();
    this.attachRoomHandlers();

    this.scale.on('resize', this.resizeHandler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
    this.fadeTickerHandle = window.setInterval(() => this.tickChatFade(), 250);
    this.layout();
  }

  private goTo(key: string, data?: object): void {
    this.cleanup();
    transitionTo(this, key, data);
  }

  private layout(): void {
    const { width, height } = this.scale;
    // Responsive scale: shrink everything to fit small (landscape) screens.
    this.uiScale = Math.max(
      0.45,
      Math.min(1, Math.min(width / 1180, height / 760)),
    );
    this.handScale = Math.min(1, this.uiScale * 1.4);
    this.seatScale = Math.min(1, this.uiScale * 1.6) * 0.9;
    document.documentElement.style.setProperty(
      '--game-scale',
      String(this.uiScale),
    );
    // Interactive HUD (buttons, chat input, toolbar) shrinks far less than the
    // decorative chrome so it stays comfortably tappable on phones.
    document.documentElement.style.setProperty(
      '--hud-scale',
      String(Math.min(1, this.uiScale * 1.7)),
    );
    const ch = CARD_HEIGHT * this.handScale;
    const { x: cx, y: cy } = this.board.getCenter();
    this.phaseBanner.setPosition(cx, 22);
    this.statusBanner.setPosition(cx, 54);
    this.revBanner.setPosition(cx, height / 2);
    this.handLayer.setPosition(0, height - ch / 2 - 16 * this.handScale);
    this.trickLayer.setPosition(cx, cy);
    // Float the trick info just above the played-card pile (semi-transparent).
    if (this.trickPanel) {
      const pileHalf = (CARD_HEIGHT * 1.7 * this.uiScale) / 2;
      this.trickPanel.style.top = `${Math.max(8, cy - pileHalf - 30 * this.uiScale - 14)}px`;
    }
    this.renderHand();
    this.renderSeatsAndPlays();
    this.renderTrickPile();
    // renderHand created fresh sprites — re-apply the disabled/dim state so
    // un-playable cards stay greyed out after a relayout/resize.
    this.refreshHandPlayability();
    this.updateOrientationHint();
    void width;
  }

  private attachRoomHandlers(): void {
    const room = this.room;

    room.onMessage(SERVER_MESSAGES.HAND_UPDATE, (msg: ServerHandUpdateMessage) => {
      const oldIds = new Set(this.hand.map((c) => c.id));
      this.hand = msg.cardIds.map((id, i) => ({
        id,
        rank: msg.ranks[i] ?? 0,
      }));
      const newIds = new Set(this.hand.map((c) => c.id));
      for (const id of this.selectedIds) {
        if (!newIds.has(id)) this.selectedIds.delete(id);
      }
      const isFreshDeal = oldIds.size === 0 && newIds.size > 0;
      this.renderHand(isFreshDeal);
      this.updateMyCardPanel();
    });

    room.onMessage(SERVER_MESSAGES.CHAT, (msg: ServerChatMessage) => {
      this.pushChatLine(`${msg.fromName}: ${msg.text}`, '#3a2410');
    });
    room.onMessage(SERVER_MESSAGES.GAME_LOG, (msg: ServerGameLogMessage) => {
      this.pushGameLog(msg.text);
    });
    room.onMessage(SERVER_MESSAGES.ERROR, (msg: { code: string; text: string }) => {
      this.pushChatLine(`! ${msg.code}: ${msg.text}`, '#a72424');
      this.cameras.main.shake(150, 0.005);
      sfx.error();
    });

    room.onMessage(SERVER_MESSAGES.ROOM_STATE, (msg: ServerRoomStateMessage) => {
      this.roomState = msg;
      // Authoritative trick reset — if the server reports the trick as empty,
      // clear our cached currentTrick even if we missed the TRICK_CLEAR
      // broadcast (which can happen during scene transitions).
      if (msg.trick?.isEmpty) {
        this.currentTrick = null;
      }
      this.onStateChange();
    });

    room.onMessage(SERVER_MESSAGES.PLAY_EVENT, (msg: ServerPlayEventMessage) => {
      this.currentTrick = {
        rank: msg.rank,
        count: msg.count,
        jokers: msg.jokers,
      };
      const origin = this.getSeatWorldPos(msg.seat);
      this.renderTrickPile(origin);
      this.renderSeatsAndPlays();
      this.refreshTrickPanel();
      this.refreshHandPlayability();
      this.refreshActionButtons();
      sfx.cardPlay();
      this.showActionShout(msg.seat, this.formatPlayShout(msg), 'play');
    });
    room.onMessage(SERVER_MESSAGES.PASS_EVENT, (msg: ServerPassEventMessage) => {
      this.showActionShout(msg.seat, 'Pass!!', 'pass');
    });
    room.onMessage(SERVER_MESSAGES.TRICK_CLEAR, () => {
      this.animateTrickClear();
      this.currentTrick = null;
      this.refreshTrickPanel();
      this.refreshHandPlayability();
      this.refreshActionButtons();
    });

    room.onLeave(() => this.goTo('Lobby'));

    // GameScene just mounted. Ask the server to resend the hand + state in
    // case the initial HAND_UPDATE / ROOM_STATE arrived during the
    // RoomScene→GameScene transition window.
    room.send(CLIENT_MESSAGES.REQUEST_GAME_STATE, {});
  }

  private onStateChange(): void {
    const state = this.readState();
    if (!state) return;

    if (this.mySeat === -1) {
      const me = state.seatOrder.find((p) => p.sessionId === this.room.sessionId);
      if (me) this.mySeat = me.seat;
    }

    if (state.phase === 'finished' && !this.finishedReturnScheduled) {
      this.finishedReturnScheduled = true;
      const me = state.seatOrder.find((p) => p.seat === this.mySeat);
      const titleKor = me ? this.titleKorean(me.title) : '';
      const wonMatch = me?.title === 'great_dalmuti';
      this.showResultBanner(
        '🏆 매치 종료',
        titleKor ? `최종 직위: ${titleKor}` : '결과 집계 중…',
        5500,
        wonMatch,
      );
      this.statusBanner.setText('매치 종료 — 곧 로비로 돌아갑니다');
      this.time.delayedCall(6000, () => this.goTo('Lobby'));
    }

    if (state.phase === 'round_end') {
      const me = state.seatOrder.find((p) => p.seat === this.mySeat);
      const myPlacement = me?.lastRoundPlacement ?? -1;
      if (myPlacement >= 0 && myPlacement !== this.lastShownRoundEnd) {
        this.lastShownRoundEnd = myPlacement;
        const isWinner = myPlacement === 0;
        const titleKor = me ? this.titleKorean(me.title) : '';
        this.showResultBanner(
          isWinner ? '🥇 라운드 승리!' : `라운드 ${myPlacement + 1}등`,
          titleKor ? `다음 라운드 직위: ${titleKor}` : '',
          3200,
          isWinner,
        );
      }
    } else if (state.phase === 'playing' || state.phase === 'tax') {
      // Reset so future round_ends trigger again.
      if (state.phase === 'playing') this.lastShownRoundEnd = -1;
    }

    this.phaseBanner.setText(phaseLabel(state.phase));
    const cur = state.seatOrder.find((p) => p.seat === state.currentSeat);
    const isMyTurn = state.currentSeat === this.mySeat && state.phase === 'playing';
    if (isMyTurn && !this.wasMyTurn) sfx.yourTurn();
    this.wasMyTurn = isMyTurn;
    if (state.phase === 'round_end' && this.lastSeenPhase !== 'round_end') {
      sfx.roundEnd();
    }
    if (state.phase === 'finished' && this.lastSeenPhase !== 'finished') {
      sfx.matchEnd();
    }
    // Play the dealing animation once, when we first enter the dealing phase.
    if (state.phase === 'dealing' && this.lastSeenPhase !== 'dealing') {
      this.playDealAnimation(state);
    }
    this.lastSeenPhase = state.phase;
    let statusText: string;
    if (state.phase === 'playing' && cur) {
      statusText = isMyTurn ? '내 차례입니다' : `${cur.displayName} 차례`;
    } else if (state.phase === 'waiting') {
      statusText = `${state.seatOrder.length}명 입장 · 4명이 되면 시작합니다`;
    } else if (state.phase === 'revolution') {
      statusText =
        state.revolutionSeat === this.mySeat
          ? '⚔ 조커 2장! 혁명 선언 시 상납 취소 · 꼴등이 선(先) · 역순 진행'
          : '혁명 선언 대기 중...';
    } else if (state.phase === 'tax') {
      const me = state.seatOrder.find((p) => p.seat === this.mySeat);
      if (me && me.pendingTaxCount > 0) {
        const target = state.seatOrder.find((p) => p.seat === me.pendingTaxTo);
        statusText = `${target?.displayName ?? '농노'}에게 ${me.pendingTaxCount}장 하사 — 카드를 선택해 보내세요`;
      } else {
        statusText = '상납 단계 — 달무티가 카드 선택 중...';
      }
    } else if (state.phase === 'dealing') {
      statusText = '🎴 패를 나눠주는 중...';
    } else if (state.phase === 'round_end') {
      const amHost = state.hostSessionId === this.room.sessionId;
      statusText = amHost
        ? '라운드 종료 — 다음 게임을 시작하세요'
        : '라운드 종료 — 방장이 다음 게임을 시작하길 기다리는 중';
    } else if (state.phase === 'finished') {
      statusText = '매치 종료 — 곧 로비로 돌아갑니다';
    } else {
      statusText = '';
    }
    if (this.isSpectator()) {
      this.statusBanner.setText('🔍 관전 중');
      this.updateStatusBadge('관전 중', false);
    } else {
      this.statusBanner.setText(statusText);
      this.updateStatusBadge(statusText, isMyTurn);
    }

    this.revolutionActive = state.revolutionActive;
    if (state.revolutionActive) {
      this.revBanner.setText('REVOLUTION!');
      if (this.revBanner.alpha === 0) {
        this.tweens.add({
          targets: this.revBanner,
          alpha: 1,
          duration: 200,
          yoyo: true,
          repeat: 2,
          onComplete: () => this.revBanner.setAlpha(0.9),
        });
      }
    } else {
      this.revBanner.setAlpha(0);
    }

    this.renderSeatsAndPlays();
    this.refreshHandPlayability();
    this.refreshActionButtons();
    this.updateNextGameButton(state);
    this.updateSkipButton(state);
    this.updateMyCardPanel();
  }

  private readState(): RoomStateView | null {
    const s = this.roomState;
    if (!s) return null;
    return {
      phase: s.phase,
      currentSeat: s.currentSeat,
      revolutionActive: s.revolutionActive,
      revolutionSeat: s.revolutionSeat ?? -1,
      hostSessionId: s.hostSessionId,
      seatOrder: s.seatOrder.map((p) => ({
        seat: p.seat,
        sessionId: p.sessionId,
        displayName: p.displayName,
        handCount: p.handCount,
        isBot: p.isBot,
        title: p.title,
        hasPassed: p.hasPassed,
        pendingTaxCount: p.pendingTaxCount,
        pendingTaxTo: p.pendingTaxTo,
        lastRoundPlacement: p.lastRoundPlacement,
        turnOrder: p.turnOrder,
      })),
      trick: s.trick,
      spectators: s.spectators ?? [],
      canSkip: s.canSkip ?? false,
    };
  }

  private renderHand(animate = false): void {
    this.handLayer.removeAll(true);
    this.cardSprites.clear();
    const sorted = this.hand.slice().sort((a, b) => a.rank - b.rank);
    const n = sorted.length;
    if (n === 0) return;
    const s = this.uiScale;
    const hs = this.handScale;
    const cw = CARD_WIDTH * hs;
    const ch = CARD_HEIGHT * hs;
    // Keep clear of the action buttons (PASS) on the right; small left gutter.
    const leftReserve = 16 * s;
    const rightReserve = 210 * s;
    const availW = Math.max(
      this.scale.width - leftReserve - rightReserve - 20,
      cw * 2,
    );
    const spacing = Math.min(cw - 4 * hs, availW / Math.max(n, 1));
    const totalWidth = (n - 1) * spacing;
    const handCenterX =
      leftReserve + (this.scale.width - leftReserve - rightReserve) / 2;
    const startX = handCenterX - totalWidth / 2;

    const panelW = this.drawHandPanel(totalWidth + cw, handCenterX);
    // The hand panel is centered on handCenterX, so its right edge moves
    // inward as the hand shrinks. Anchor the action buttons to that edge.
    this.handPanelRightX = handCenterX + panelW / 2;
    this.positionActionButtons();
    sorted.forEach((card, i) => {
      const sprite = new CardSprite(this, { rank: card.rank, width: cw, height: ch });
      sprite.setPosition(startX + i * spacing, 0);
      sprite.setSelected(this.selectedIds.has(card.id));
      // No setInteractive here. Hit detection is done at scene level in
      // setupHandPointerInput() — Phaser Container's hit area was offset
      // by displayOrigin and unreliable for overlapping hand cards.
      if (this.selectedIds.has(card.id)) sprite.y = -22 * hs;
      this.handLayer.add(sprite);
      this.cardSprites.set(card.id, sprite);
      if (animate) {
        const finalX = sprite.x;
        const finalY = sprite.y;
        const deckX = this.scale.width / 2 - this.handLayer.x;
        const deckY = this.board.getCenter().y - this.handLayer.y;
        sprite.setPosition(deckX, deckY);
        sprite.alpha = 0;
        sprite.scale = 0.6;
        sprite.rotation = (Math.random() - 0.5) * 0.5;
        this.tweens.add({
          targets: sprite,
          x: finalX,
          y: finalY,
          alpha: 1,
          scale: 1,
          rotation: 0,
          duration: 320,
          delay: i * 35,
          ease: 'Cubic.easeOut',
          onStart: () => sfx.dealTick(),
        });
      }
    });
  }

  private renderSeatsAndPlays(): void {
    const state = this.readState();
    if (!state) return;
    const seatRows = state.seatOrder;
    const myIdx = Math.max(0, seatRows.findIndex((p) => p.seat === this.mySeat));
    const aliveSeats = new Set(seatRows.map((p) => p.seat));
    const root = document.getElementById('overlay');

    seatRows.forEach((p, i) => {
      const relativeIdx = (i - myIdx + seatRows.length) % seatRows.length;
      const isMe = p.seat === this.mySeat;
      const seatPos = this.board.getSeatPosition(relativeIdx, seatRows.length);
      const drop = this.board.getPlayDropPosition(relativeIdx, seatRows.length);

      if (!isMe) {
        let badge = this.seatBadges.get(p.seat);
        if (!badge && root) {
          badge = new SeatDomBadge(root);
          this.seatBadges.set(p.seat, badge);
        }
        if (badge) {
          badge.setScale(this.seatScale);
          badge.setPosition(seatPos.x, seatPos.y);
          badge.update({
            displayName: p.displayName,
            handCount: p.handCount,
            title: p.title,
            isCurrentTurn: p.seat === state.currentSeat && state.phase === 'playing',
            hasPassed: p.hasPassed,
            isBot: p.isBot,
            turnOrder: p.turnOrder,
            lastRoundPlacement: p.lastRoundPlacement,
          });
        }
      } else {
        const existingBadge = this.seatBadges.get(p.seat);
        if (existingBadge) {
          existingBadge.destroy();
          this.seatBadges.delete(p.seat);
        }
      }

      void drop;
    });

    for (const seat of [...this.seatBadges.keys()]) {
      if (!aliveSeats.has(seat)) {
        this.seatBadges.get(seat)?.destroy();
        this.seatBadges.delete(seat);
      }
    }
  }

  private renderTrickPile(originWorld?: { x: number; y: number }): void {
    this.trickLayer.removeAll(true);
    const trick = this.currentTrick;
    if (!trick) return;
    const cardScale = 1.7 * this.uiScale;
    const cardW = CARD_WIDTH * cardScale;
    const spacing = cardW * 0.45;
    const startX = -((trick.count - 1) * spacing) / 2;
    const layerX = this.trickLayer.x;
    const layerY = this.trickLayer.y;
    for (let i = 0; i < trick.count; i++) {
      const isJoker = i >= trick.count - trick.jokers;
      const card = new CardSprite(this, {
        rank: isJoker ? JOKER_RANK : trick.rank,
        scale: cardScale,
      });
      const targetX = startX + i * spacing;
      const targetY = 0;
      const tiltDeg = (i - (trick.count - 1) / 2) * 5;
      const targetRot = (tiltDeg * Math.PI) / 180;
      if (originWorld) {
        // Start at the origin (seat / hand), interpolate to trick position.
        const sx = originWorld.x - layerX;
        const sy = originWorld.y - layerY;
        card.setPosition(sx, sy);
        card.setRotation((Math.random() - 0.5) * 0.4);
        card.setAlpha(0.85);
        this.trickLayer.add(card);
        this.tweens.add({
          targets: card,
          x: targetX,
          y: targetY,
          rotation: targetRot,
          alpha: 1,
          duration: 280,
          delay: i * 35,
          ease: 'Cubic.easeOut',
        });
      } else {
        card.setPosition(targetX, targetY);
        card.setRotation(targetRot);
        this.trickLayer.add(card);
      }
    }
  }

  private animateTrickClear(): void {
    if (this.trickLayer.length === 0) return;
    const children = this.trickLayer.list.slice();
    const layerToDestroy = this.trickLayer;
    // Replace the active trick layer immediately so a new play can start.
    this.trickLayer = this.add.container(layerToDestroy.x, layerToDestroy.y);
    this.trickLayer.setDepth(35);
    for (const child of children) {
      this.tweens.add({
        targets: child,
        alpha: 0,
        y: (child as Phaser.GameObjects.Container).y + 28,
        rotation: ((Math.random() - 0.5) * 0.5),
        duration: 260,
        ease: 'Cubic.easeIn',
        onComplete: () => child.destroy(),
      });
    }
    this.time.delayedCall(320, () => layerToDestroy.destroy());
  }

  private formatPlayShout(msg: ServerPlayEventMessage): string {
    const countWord = ['', '한장', '두장', '세장', '네장', '다섯장', '여섯장', '일곱장'];
    const cw = countWord[msg.count] ?? `${msg.count}장`;
    if (msg.rank === JOKER_RANK && msg.jokers === msg.count) {
      return msg.count === 1 ? '조커!' : `조커 ${cw}!`;
    }
    const rankLabel = msg.rank === JOKER_RANK ? '조커' : `${msg.rank}`;
    const jokerTail = msg.jokers > 0 ? ` +🃏${msg.jokers}` : '';
    return `${rankLabel} ${cw}!${jokerTail}`;
  }

  private showActionShout(seat: number, text: string, kind: 'play' | 'pass'): void {
    const pos = this.getSeatWorldPos(seat);
    const isMe = seat === this.mySeat;
    const center = this.board.getCenter();
    const startX = pos.x;
    const startY = pos.y - (isMe ? CARD_HEIGHT * 0.6 * this.uiScale : 50 * this.uiScale);
    const endX = isMe ? startX : (startX + center.x) / 2;
    const endY = isMe ? startY - 70 : (startY + center.y) / 2;

    const overlay = document.getElementById('overlay');
    const canvas = this.game.canvas;
    if (!overlay || !canvas) return;
    const rect = canvas.getBoundingClientRect();

    const color = kind === 'pass' ? '#9fb8d8' : '#ffe27a';
    const stroke = kind === 'pass' ? '#1a2740' : '#5a2a00';
    const startAngle = Math.floor(Math.sin(startX + startY) * 12);
    const endAngle = startAngle * 0.3;
    const startScale = 0.2;
    const peakScale = 1.15;
    const restScale = isMe ? 1.0 : 0.85;

    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = `
      position: absolute;
      left: ${rect.left + startX}px;
      top: ${rect.top + startY}px;
      transform: translate(-50%, -50%) scale(${startScale}) rotate(${startAngle}deg);
      font-family: "Bangers", "Bagel Fat One", "Jua", "Black Han Sans", system-ui, sans-serif;
      font-size: ${kind === 'pass' ? 46 : 52}px;
      font-weight: 900;
      color: ${color};
      -webkit-text-stroke: 4px ${stroke};
      text-shadow: 2px 4px 6px rgba(0,0,0,0.55);
      letter-spacing: 1px;
      white-space: nowrap;
      pointer-events: none;
      z-index: 1000;
      opacity: 0;
      will-change: transform, opacity;
    `;
    overlay.appendChild(el);

    const driftDuration = isMe ? 2700 : 2550;
    const driftDelay = isMe ? 1140 : 0;
    const popInMs = 180;
    const settleMs = 120;
    const totalLife = Math.max(popInMs + settleMs, driftDelay + driftDuration) + 60;

    // Keyframe schedule. Pop-in + settle scale happen at the start; drift +
    // fade happens over the rest, starting at driftDelay.
    const popInEnd = popInMs / totalLife;
    const settleEnd = (popInMs + settleMs) / totalLife;
    const driftStart = driftDelay / totalLife;

    const startTransform = `translate(-50%, -50%) scale(${startScale}) rotate(${startAngle}deg)`;
    const peakTransform = `translate(-50%, -50%) scale(${peakScale}) rotate(${startAngle}deg)`;
    const restTransform = `translate(-50%, -50%) scale(${restScale}) rotate(${startAngle}deg)`;
    const driftDx = endX - startX;
    const driftDy = endY - startY;
    const endTransform = `translate(calc(-50% + ${driftDx}px), calc(-50% + ${driftDy}px)) scale(${restScale}) rotate(${endAngle}deg)`;

    const keyframes: Keyframe[] = [
      { offset: 0, transform: startTransform, opacity: 0 },
      { offset: Math.min(0.001, popInEnd / 2), transform: startTransform, opacity: 1 },
      { offset: popInEnd, transform: peakTransform, opacity: 1 },
      { offset: settleEnd, transform: restTransform, opacity: 1 },
    ];
    if (driftStart > settleEnd) {
      keyframes.push({ offset: driftStart, transform: restTransform, opacity: 1 });
    }
    keyframes.push({ offset: 1, transform: endTransform, opacity: 0 });

    const anim = el.animate(keyframes, {
      duration: totalLife,
      easing: 'ease-out',
      fill: 'forwards',
    });
    anim.onfinish = () => el.remove();
    anim.oncancel = () => el.remove();
  }

  // Visible deal: card backs fly from a center deck out to every seat,
  // round-robin, so the random distribution reads as fair to everyone.
  private playDealAnimation(state: RoomStateView): void {
    const center = this.board.getCenter();
    const seats = state.seatOrder;
    if (seats.length === 0) return;
    const s = this.uiScale;
    sfx.deal();
    // A clearly visible deck at the center, gently pulsing.
    const deck = this.add
      .image(center.x, center.y, 'card-back')
      .setDepth(70)
      .setDisplaySize(CARD_WIDTH * s, CARD_HEIGHT * s);
    this.tweens.add({
      targets: deck,
      scale: deck.scale * 1.06,
      duration: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    const passes = 2;
    const step = 135;
    const flight = 560;
    let delay = 0;
    for (let pass = 0; pass < passes; pass++) {
      for (const p of seats) {
        const at = delay;
        const seatNo = p.seat;
        this.time.delayedCall(at, () => {
          const target = this.getSeatWorldPos(seatNo);
          const card = this.add
            .image(center.x, center.y, 'card-back')
            .setDepth(72)
            .setDisplaySize(CARD_WIDTH * 0.9 * s, CARD_HEIGHT * 0.9 * s)
            .setAngle((Math.random() - 0.5) * 16);
          sfx.dealTick();
          this.tweens.add({
            targets: card,
            x: target.x + (Math.random() - 0.5) * 26,
            y: target.y + (Math.random() - 0.5) * 18,
            angle: (Math.random() - 0.5) * 48,
            scale: card.scale * 0.8,
            alpha: 0,
            duration: flight,
            ease: 'Cubic.easeIn',
            onComplete: () => card.destroy(),
          });
        });
        delay += step;
      }
    }
    this.time.delayedCall(delay + flight + 150, () => {
      this.tweens.killTweensOf(deck);
      this.tweens.add({
        targets: deck,
        scale: 0,
        alpha: 0,
        duration: 220,
        onComplete: () => deck.destroy(),
      });
    });
  }

  // Fullscreen toggle — primarily for mobile landscape, where the browser
  // chrome eats vertical space. Only shown on touch devices that actually
  // support the Fullscreen API (iPhone Safari does not, so it stays hidden
  // there rather than offering a button that does nothing).
  private setupFullscreenButton(): void {
    const target = document.documentElement;
    const supported = typeof target.requestFullscreen === 'function';
    const isTouch =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    // Only worth showing on touch devices that actually support the API.
    if (!supported || !isTouch) return;
    const root = document.getElementById('overlay');
    if (!root) return;

    const btn = document.createElement('button');
    btn.id = 'dm-fullscreen';
    btn.textContent = '⛶';
    btn.style.cssText = `
      position: absolute; top: 18px; left: 24px;
      width: 40px; height: 40px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(180deg, rgba(40,18,22,0.95), rgba(26,8,10,0.95));
      border: 1px solid rgba(245,200,105,0.4);
      color: #f5c869; font-size: 18px; cursor: pointer;
      box-shadow: 0 4px 10px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,220,150,0.15);
      pointer-events: auto; z-index: 70;
      transform-origin: top left; transform: scale(var(--hud-scale, 1));
    `;
    root.appendChild(btn);
    this.fullscreenButton = btn;

    const sync = () => {
      const on = !!document.fullscreenElement;
      btn.textContent = on ? '⤡' : '⛶';
      btn.title = on ? '전체화면 종료' : '전체화면';
    };
    this.fsChangeHandler = sync;
    document.addEventListener('fullscreenchange', sync);

    btn.addEventListener('click', () => {
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      } else {
        target.requestFullscreen?.().catch(() => {});
      }
    });
    sync();
  }

  private mountNextGameButton(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const btn = document.createElement('button');
    btn.id = 'dm-next-game';
    btn.textContent = '🎴 다음 게임 시작';
    btn.style.cssText = `
      position:absolute; left:50%; top:60%; transform:translateX(-50%) scale(var(--hud-scale,1)); transform-origin:top center;
      padding:16px 38px; border-radius:16px;
      background: linear-gradient(180deg,#b8e8a8 0%,#7ec96a 100%);
      border:3px solid #4f8a3c; color:#173a10;
      font-family:'Apple SD Gothic Neo','Segoe UI',sans-serif; font-size:18px; font-weight:800;
      letter-spacing:0.5px; cursor:pointer; pointer-events:auto; z-index:80;
      box-shadow:0 8px 0 #4f8a3c, 0 12px 24px rgba(0,0,0,0.5);
      display:none;
    `;
    btn.addEventListener('click', () => {
      btn.style.display = 'none';
      this.room.send(CLIENT_MESSAGES.START_GAME, {});
    });
    root.appendChild(btn);
    this.nextGameButton = btn;
  }

  // Shown only when every remaining player is a bot — lets a human fast-forward
  // the bot-only endgame instead of waiting it out at normal speed.
  private mountSkipButton(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const btn = document.createElement('button');
    btn.id = 'dm-skip-bots';
    btn.textContent = '⏩ 스킵하기';
    btn.style.cssText = `
      position:absolute; left:50%; bottom:calc(150px + var(--kb-inset,0px));
      transform:translateX(-50%) scale(var(--hud-scale,1)); transform-origin:bottom center;
      padding:12px 30px; border-radius:14px;
      background: linear-gradient(180deg,#3a2a3e 0%,#241522 100%);
      border:2px solid rgba(245,200,105,0.5); color:#f5d98a;
      font-family:'Apple SD Gothic Neo','Segoe UI',sans-serif; font-size:16px; font-weight:800;
      letter-spacing:0.5px; cursor:pointer; pointer-events:auto; z-index:80;
      box-shadow:0 6px 18px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.12);
      display:none;
    `;
    btn.addEventListener('click', () => {
      btn.style.display = 'none';
      this.room.send(CLIENT_MESSAGES.SKIP_BOTS, {});
    });
    root.appendChild(btn);
    this.skipButton = btn;
  }

  private updateSkipButton(state: RoomStateView): void {
    const btn = this.skipButton;
    if (!btn) return;
    btn.style.display =
      state.canSkip && !this.isSpectator() ? 'block' : 'none';
  }

  // The game table needs landscape. On a portrait phone, cover it with a hint.
  private mountOrientationHint(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const el = document.createElement('div');
    el.style.cssText = `
      position: fixed; inset: 0; z-index: 300;
      display: none; flex-direction: column; gap: 14px;
      align-items: center; justify-content: center;
      background: radial-gradient(ellipse at center, #5a1c20 0%, #2c0b0e 100%);
      color: #f5c869; text-align: center; pointer-events: auto;
      font-family: "Apple SD Gothic Neo","Segoe UI",sans-serif;
    `;
    el.innerHTML = `
      <div style="font-size:54px;">📱↻</div>
      <div style="font-size:20px; font-weight:800;">가로로 돌려주세요</div>
      <div style="font-size:13px; color:rgba(244,236,214,0.7);">달무티 게임은 가로 화면에 최적화되어 있습니다</div>
    `;
    root.appendChild(el);
    this.orientationHint = el;
    this.updateOrientationHint();
  }

  private updateOrientationHint(): void {
    const el = this.orientationHint;
    if (!el) return;
    const portrait = window.innerHeight > window.innerWidth;
    const phone = Math.min(window.innerWidth, window.innerHeight) < 600;
    el.style.display = portrait && phone ? 'flex' : 'none';
  }

  // The host gets a "deal the next game" button once a round has ended.
  private updateNextGameButton(state: RoomStateView): void {
    const btn = this.nextGameButton;
    if (!btn) return;
    const amHost = state.hostSessionId === this.room.sessionId;
    const show =
      state.phase === 'round_end' && amHost && !this.isSpectator();
    btn.style.display = show ? 'block' : 'none';
  }

  private getSeatWorldPos(seat: number): { x: number; y: number } {
    const state = this.readState();
    if (!state) return this.board.getCenter();
    if (seat === this.mySeat) {
      // Local hand origin — the middle of the hand panel.
      return {
        x: this.scale.width / 2,
        y: this.handLayer.y,
      };
    }
    const seatRows = state.seatOrder;
    const myIdx = Math.max(0, seatRows.findIndex((p) => p.seat === this.mySeat));
    const i = seatRows.findIndex((p) => p.seat === seat);
    if (i < 0) return this.board.getCenter();
    const relativeIdx = (i - myIdx + seatRows.length) % seatRows.length;
    return this.board.getSeatPosition(relativeIdx, seatRows.length);
  }

  private drawHandPanel(spannedWidth: number, centerX: number): number {
    const g = this.add.graphics();
    const s = this.handScale;
    const padX = 26 * s;
    const padY = 16 * s;
    const maxW = this.scale.width - 200 * this.uiScale; // clear of the action buttons
    const w = Math.min(spannedWidth, maxW) + padX * 2;
    const h = CARD_HEIGHT * s + padY * 2;
    const x = -w / 2;
    const y = -h / 2;
    g.setPosition(centerX, 0);
    // drop shadow
    g.fillStyle(0x3a2410, 0.35);
    g.fillRoundedRect(x + 4, y + 8, w, h, 18);
    // outer wood
    g.fillStyle(0x8a5e1f, 1);
    g.fillRoundedRect(x, y, w, h, 18);
    // inner cream face
    g.fillStyle(0xfdf4dd, 1);
    g.fillRoundedRect(x + 4, y + 4, w - 8, h - 8, 16);
    // gold inset border
    g.lineStyle(2, 0xc89b2f, 0.85);
    g.strokeRoundedRect(x + 8, y + 8, w - 16, h - 16, 14);
    this.handLayer.add(g);
    return w;
  }

  // Keep the PASS / 하사 / 혁명 buttons glued to the right edge of the hand
  // panel so they slide inward as the hand (and thus the panel) shrinks,
  // instead of stranding them at a fixed right margin. When the hand is wide
  // they clamp to the default right margin so they never run off-screen.
  private positionActionButtons(): void {
    const wrap = this.passWrap;
    if (!wrap) return;
    // Anchor the action buttons just to the RIGHT of the hand panel's right
    // edge (not the screen edge), at hand level, scaled with the UI.
    const s = this.uiScale;
    const anchor =
      this.handPanelRightX > 0
        ? this.handPanelRightX
        : this.scale.width - 220 * s;
    wrap.style.right = 'auto';
    wrap.style.left = `${anchor + 10 * s}px`;
    wrap.style.bottom = `${(CARD_HEIGHT * this.handScale) / 2 + 14}px`;
    wrap.style.transformOrigin = 'bottom left';
    wrap.style.transform = `scale(${s})`;
  }

  private setupHandPointerInput(): void {
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      const hovered = this.cardAtPointer(pointer);
      for (const [id, sprite] of this.cardSprites) {
        const wantHover = hovered?.id === id && !sprite.isDisabled();
        sprite.setHover(wantHover);
      }
    });
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const card = this.cardAtPointer(pointer);
      if (card) {
        // Only record the drag-start. We DON'T toggle here: toggling on
        // pointerdown meant that grabbing an already-selected card to drag it
        // out would immediately deselect it (and break the selection). The
        // toggle is now deferred to pointerup, where we can tell a tap (toggle)
        // apart from a drag-up (play).
        this.dragStartY = pointer.y;
        this.dragStartCardId = card.id;
      } else {
        this.dragStartY = null;
        this.dragStartCardId = null;
      }
    });
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.dragStartY === null || this.dragStartCardId === null) return;
      const dy = this.dragStartY - pointer.y; // positive = upward
      const cardId = this.dragStartCardId;
      this.dragStartY = null;
      this.dragStartCardId = null;
      // Threshold: ~one third of a card height. Below that we treat it as a
      // tap and just toggle the card's selection.
      const dragged = dy >= CARD_HEIGHT * 0.4 * this.handScale;
      if (!dragged) {
        this.toggleSelect(cardId);
        return;
      }
      // Drag upward = play. Pull the grabbed card into the selection if it
      // wasn't already part of it, but never deselect a card just because the
      // drag happened to start on top of it.
      if (!this.selectedIds.has(cardId)) this.toggleSelect(cardId);
      if (!this.canPlaySelection()) return;
      this.playSelected();
    });
  }

  private cardAtPointer(pointer: Phaser.Input.Pointer): HandCard | null {
    if (this.hand.length === 0) return null;
    const handY = this.handLayer.y;
    const localY = pointer.y - handY;
    const sorted = this.hand.slice().sort((a, b) => a.rank - b.rank);
    // Iterate top-most card first (last added = drawn on top).
    for (let i = sorted.length - 1; i >= 0; i--) {
      const card = sorted[i];
      if (!card) continue;
      const sprite = this.cardSprites.get(card.id);
      if (!sprite) continue;
      const dx = pointer.x - sprite.x;
      if (Math.abs(dx) > (CARD_WIDTH * this.handScale) / 2) continue;
      const dy = localY - sprite.y;
      if (Math.abs(dy) > (CARD_HEIGHT * this.handScale) / 2) continue;
      return card;
    }
    return null;
  }

  // Compact self-status chip in the top-right toolbar (직위 · 남은 카드).
  // Replaces the old bottom-left MY STATUS panel that overlapped the hand.
  private updateMyCardPanel(): void {
    const chip = this.myCardPanel;
    if (!chip) return;
    const titleEl = chip.querySelector<HTMLSpanElement>('#sc-title');
    const countEl = chip.querySelector<HTMLSpanElement>('#sc-count');
    if (!titleEl || !countEl) return;
    const state = this.readState();
    const me = state?.seatOrder.find((p) => p.seat === this.mySeat);
    if (!me) {
      titleEl.textContent = '직위 미정';
      countEl.textContent = '🃏 -';
      return;
    }
    const titleKor = this.titleKorean(me.title);
    titleEl.textContent = titleKor || '직위 미정';
    countEl.textContent = `🃏 ${me.handCount}장`;
  }

  private titleKorean(t: string): string {
    switch (t) {
      case 'great_dalmuti':
        return '대달무티';
      case 'lesser_dalmuti':
        return '부달무티';
      case 'merchant':
        return '상인';
      case 'lesser_peon':
        return '소농';
      case 'greater_peon':
        return '대농노';
      default:
        return '';
    }
  }

  private mountResultBanner(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const el = document.createElement('div');
    el.style.cssText = `
      position: absolute;
      left: 50%;
      top: 38%;
      transform: translate(-50%, -50%) scale(0.6);
      padding: 28px 56px;
      background: linear-gradient(180deg, #fff4c2 0%, #ffd86b 100%);
      border: 3px solid #8a5e1f;
      border-radius: 22px;
      box-shadow: 0 18px 60px rgba(58,36,16,0.55), inset 0 1px 0 rgba(255,255,255,0.7);
      font-family: Georgia, serif;
      text-align: center;
      pointer-events: none;
      z-index: 80;
      opacity: 0;
      transition: opacity 0.35s, transform 0.35s;
    `;
    root.appendChild(el);
    this.resultBanner = el;
  }

  private showResultBanner(
    titleText: string,
    subText: string,
    ms = 3200,
    celebrate = false,
  ): void {
    if (!this.resultBanner) this.mountResultBanner();
    const el = this.resultBanner;
    if (!el) return;
    el.innerHTML = `
      <div style="font-size:42px; font-weight:bold; color:#5a3008;">${titleText}</div>
      <div style="font-size:18px; color:#7a4a14; margin-top:8px;">${subText}</div>
    `;
    const s = this.uiScale;
    el.style.opacity = '1';
    el.style.transform = `translate(-50%, -50%) scale(${s})`;
    if (celebrate) {
      sfx.fanfare();
      this.launchFireworks();
    }
    window.setTimeout(() => {
      if (!this.resultBanner) return;
      this.resultBanner.style.opacity = '0';
      this.resultBanner.style.transform = `translate(-50%, -50%) scale(${s * 0.6})`;
    }, ms);
  }

  // Celebratory firework bursts on the canvas (behind the DOM result banner).
  private launchFireworks(): void {
    const colors = [0xffd86b, 0xff7a7a, 0x82dd9c, 0x6db4e0, 0xf5c869, 0xffffff];
    const bursts = 6;
    for (let b = 0; b < bursts; b++) {
      this.time.delayedCall(b * 260, () => {
        const cx = this.scale.width * (0.18 + Math.random() * 0.64);
        const cy = this.scale.height * (0.16 + Math.random() * 0.3);
        const color = colors[Math.floor(Math.random() * colors.length)]!;
        const n = 28;
        for (let i = 0; i < n; i++) {
          const angle = (Math.PI * 2 * i) / n + Math.random() * 0.18;
          const speed = 130 + Math.random() * 130;
          const dot = this.add.circle(cx, cy, 3 + Math.random() * 2, color);
          dot.setDepth(80);
          this.tweens.add({
            targets: dot,
            x: cx + Math.cos(angle) * speed,
            y: cy + Math.sin(angle) * speed + 70, // gentle gravity
            alpha: 0,
            scale: 0.2,
            duration: 950 + Math.random() * 450,
            ease: 'Quad.easeOut',
            onComplete: () => dot.destroy(),
          });
        }
      });
    }
  }

  private mountStatusBadge(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const el = document.createElement('div');
    el.style.cssText = `
      position: absolute;
      left: 50%;
      top: 24px;
      transform: translateX(-50%);
      padding: 10px 22px;
      background: linear-gradient(180deg, #fff7e0 0%, #f3e5b8 100%);
      border: 2px solid #c89b2f;
      border-radius: 22px;
      box-shadow: 0 6px 20px rgba(58,36,16,0.25), inset 0 1px 0 rgba(255,255,255,0.6);
      color: #3a2410;
      font-size: 18px;
      font-weight: bold;
      font-family: "Apple SD Gothic Neo","Segoe UI",sans-serif;
      pointer-events: none;
      letter-spacing: 0.3px;
      transition: background 0.2s, border-color 0.2s, transform 0.2s, box-shadow 0.2s;
      z-index: 40;
    `;
    root.appendChild(el);
    this.statusBadge = el;
  }

  private updateStatusBadge(text: string, isMyTurn: boolean): void {
    const el = this.statusBadge;
    if (!el) return;
    el.textContent = text;
    const s = this.uiScale;
    if (isMyTurn) {
      el.style.background =
        'linear-gradient(180deg, #fff4c2 0%, #ffd86b 100%)';
      el.style.borderColor = '#efc659';
      el.style.color = '#5a3008';
      el.style.transform = `translateX(-50%) scale(${s * 1.1})`;
      el.style.animation = 'turnPulse 1.4s ease-in-out infinite';
    } else {
      el.style.background =
        'linear-gradient(180deg, #fff7e0 0%, #f3e5b8 100%)';
      el.style.borderColor = '#c89b2f';
      el.style.color = '#3a2410';
      el.style.transform = `translateX(-50%) scale(${s})`;
      el.style.animation = 'none';
    }
  }

  private mountTrickPanel(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const el = document.createElement('div');
    el.style.cssText = `
      position: absolute;
      left: 50%;
      top: 78px;
      transform: translateX(-50%) scale(var(--game-scale, 1));
      transform-origin: top center;
      padding: 5px 16px;
      background: rgba(16, 8, 10, 0.5);
      backdrop-filter: blur(3px);
      border: 1px solid rgba(245, 200, 105, 0.3);
      border-radius: 13px;
      color: #f4ecd6;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.2px;
      text-shadow: 0 1px 2px rgba(0,0,0,0.6);
      font-family: "Apple SD Gothic Neo","Segoe UI",sans-serif;
      pointer-events: none;
      z-index: 35;
      white-space: nowrap;
    `;
    root.appendChild(el);
    this.trickPanel = el;
    this.refreshTrickPanel();
  }

  private refreshTrickPanel(): void {
    const el = this.trickPanel;
    if (!el) return;
    if (!this.currentTrick) {
      el.textContent = '리드 시작 — 어떤 묶음이든 가능';
      return;
    }
    const t = this.currentTrick;
    const rankLabel = t.rank === 13 ? '조커' : String(t.rank);
    el.textContent = `현재 트릭: ${t.count}장 · 다음은 ${t.count}장 + ${rankLabel} 미만`;
  }

  private refreshHandPlayability(): void {
    const state = this.readState();
    const isMyPlayingTurn =
      !!state && state.currentSeat === this.mySeat && state.phase === 'playing';
    const isMyTaxGift = this.myPendingTaxCount() > 0;
    const enableAll = isMyTaxGift;
    for (const [id, sprite] of this.cardSprites) {
      const card = this.hand.find((c) => c.id === id);
      if (!card) continue;
      if (enableAll) {
        sprite.setDisabled(false);
        continue;
      }
      if (!isMyPlayingTurn) {
        sprite.setDisabled(true);
        continue;
      }
      sprite.setDisabled(!this.cardIsPlayable(card.rank));
    }
  }

  private myPendingTaxCount(): number {
    const state = this.readState();
    if (!state || state.phase !== 'tax') return 0;
    const me = state.seatOrder.find((p) => p.seat === this.mySeat);
    return me?.pendingTaxCount ?? 0;
  }

  private cardIsPlayable(rank: number): boolean {
    if (rank === 13) return true;
    const trick = this.currentTrick;
    if (!trick) return true;
    // Rule 3: must beat the trick rank — always lower (stronger). Revolution
    // cancels tax; it never inverts play strength.
    if (rank >= trick.rank) return false;
    // Rule 2: must be able to fill `trick.count` cards using this rank
    //         plus any available jokers.
    const sameRankCount = this.hand.filter((c) => c.rank === rank).length;
    const jokerCount = this.hand.filter((c) => c.rank === 13).length;
    if (sameRankCount + jokerCount < trick.count) return false;
    return true;
  }

  private isSpectator(): boolean {
    if (this.mySeat !== -1) return false;
    const state = this.readState();
    return !!state?.spectators?.some(
      (s) => s.sessionId === this.room.sessionId,
    );
  }

  private canPlaySelection(): boolean {
    if (this.selectedIds.size === 0) return false;
    const taxNeeded = this.myPendingTaxCount();
    if (taxNeeded > 0) return this.selectedIds.size === taxNeeded;
    const state = this.readState();
    const isMyTurn =
      !!state && state.currentSeat === this.mySeat && state.phase === 'playing';
    if (!isMyTurn) return false;
    const selectedCards = [...this.selectedIds]
      .map((id) => this.hand.find((c) => c.id === id))
      .filter((c): c is HandCard => !!c);
    return (
      selectedCards.length > 0 && this.selectionMatchesTrick(selectedCards)
    );
  }

  private handlePass(): void {
    // Clear any selection — passing on a half-formed bundle should not
    // leave cards highlighted afterwards.
    if (this.selectedIds.size > 0) {
      const ids = [...this.selectedIds];
      this.selectedIds.clear();
      for (const id of ids) {
        const s = this.cardSprites.get(id);
        if (s) {
          s.setSelected(false);
          this.tweens.add({
            targets: s,
            y: 0,
            duration: 120,
            ease: 'Quad.easeOut',
          });
        }
      }
    }
    this.room.send(CLIENT_MESSAGES.PASS, {});
    sfx.pass();
    this.refreshActionButtons();
  }

  private refreshActionButtons(): void {
    const state = this.readState();
    const spectating = this.isSpectator();
    if (this.actionPanel)
      this.actionPanel.style.display = spectating ? 'none' : '';
    if (this.passWrap)
      this.passWrap.style.display = spectating ? 'none' : 'block';
    if (this.myCardPanel)
      this.myCardPanel.style.display = spectating ? 'none' : 'flex';
    if (spectating) {
      this.statusBanner.setText('🔍 관전 중');
      return;
    }
    const phase = state?.phase ?? '';
    const isMyTurn = state?.currentSeat === this.mySeat && phase === 'playing';
    const myJokerCount = this.hand.filter((c) => c.rank === 13).length;
    const taxNeeded = this.myPendingTaxCount();

    // Action slot mapping:
    //  - revolution phase: [⚔ 혁명!] + [넘기기] (only for the joker-holder)
    //  - tax phase:        [🎁 N장 하사] (if I owe tribute)
    //  - playing:          [PASS!!] (only when responding to a trick on my turn)
    void myJokerCount;
    const showTaxGift = phase === 'tax' && taxNeeded > 0;
    const showRevolution =
      phase === 'revolution' && state?.revolutionSeat === this.mySeat;
    const showPass = isMyTurn && this.currentTrick !== null;
    // The 내기(play) button only appears once cards are actually selected (and
    // it's my playing turn). It sits above PASS and is enabled only while the
    // current selection is a legal play.
    const showPlay =
      isMyTurn && phase === 'playing' && this.selectedIds.size > 0;

    if (this.passButton) {
      this.passButton.style.display = showPass ? 'inline-block' : 'none';
    }
    if (this.playButton) {
      this.playButton.style.display = showPlay ? 'inline-block' : 'none';
      const canPlay = this.canPlaySelection();
      this.playButton.disabled = !canPlay;
      this.playButton.style.opacity = canPlay ? '1' : '0.5';
      this.playButton.style.cursor = canPlay ? 'pointer' : 'not-allowed';
    }
    if (this.taxGiftButton) {
      this.taxGiftButton.style.display = showTaxGift ? 'inline-block' : 'none';
      this.taxGiftButton.textContent = `🎁 ${taxNeeded}장 하사!`;
      const canGift = this.canPlaySelection();
      this.taxGiftButton.disabled = !canGift;
      this.taxGiftButton.style.opacity = canGift ? '1' : '0.55';
      this.taxGiftButton.style.cursor = canGift ? 'pointer' : 'not-allowed';
    }
    if (this.revolutionButton) {
      this.revolutionButton.style.display = showRevolution
        ? 'inline-block'
        : 'none';
    }
    if (this.declineRevolutionButton) {
      this.declineRevolutionButton.style.display = showRevolution
        ? 'inline-block'
        : 'none';
    }
    if (this.passWrap) {
      this.passWrap.style.display =
        showPlay || showPass || showRevolution || showTaxGift ? 'flex' : 'none';
      // Re-anchor now that visibility (and thus offsetWidth) is settled.
      this.positionActionButtons();
    }
  }

  private selectionMatchesTrick(cards: HandCard[]): boolean {
    if (cards.length === 0) return false;
    void cards.filter((c) => c.rank === 13);
    const nonJokers = cards.filter((c) => c.rank !== 13);
    if (nonJokers.length > 0) {
      const r = nonJokers[0]!.rank;
      if (!nonJokers.every((c) => c.rank === r)) return false;
    }
    const trick = this.currentTrick;
    if (!trick) return true;
    if (cards.length !== trick.count) return false;
    if (nonJokers.length === 0) {
      // Joker-only is declared as rank 13 (weakest) — can only lead, never beat.
      return false;
    }
    const r = nonJokers[0]!.rank;
    return r < trick.rank;
  }

  private toggleSelect(id: number): void {
    const sprite = this.cardSprites.get(id);
    if (sprite && sprite.isDisabled()) return;

    const card = this.hand.find((c) => c.id === id);
    if (!card) return;

    const taxNeeded = this.myPendingTaxCount();
    const isTaxGift = taxNeeded > 0;

    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      // Auto-bundle: clicking one card from empty selection should grab the
      // whole rank (when leading a new trick) or fill to the trick count
      // (when responding). Joker cards keep their single-card behaviour.
      const trick = this.currentTrick;
      if (
        !isTaxGift &&
        this.selectedIds.size === 0 &&
        card.rank !== 13
      ) {
        const sameRank = this.hand.filter(
          (c) => c.rank === card.rank && c.id !== id,
        );
        const jokers = this.hand.filter((c) => c.rank === 13);
        let bundle: number[] | null = null;
        if (trick && trick.count > 1) {
          // Responding: fill to trick.count using same-rank + jokers.
          const collected: number[] = [id];
          for (const c of sameRank) {
            if (collected.length >= trick.count) break;
            collected.push(c.id);
          }
          for (const c of jokers) {
            if (collected.length >= trick.count) break;
            collected.push(c.id);
          }
          if (collected.length === trick.count) bundle = collected;
        } else if (!trick && sameRank.length > 0) {
          // Leading: grab every card of this rank.
          bundle = [id, ...sameRank.map((c) => c.id)];
        }
        if (bundle) {
          for (const bid of bundle) {
            this.selectedIds.add(bid);
            const s = this.cardSprites.get(bid);
            if (s) {
              s.setSelected(true);
              this.tweens.add({
                targets: s,
                y: -22,
                duration: 120,
                ease: 'Quad.easeOut',
              });
            }
          }
          this.refreshActionButtons();
          return;
        }
      }
      if (isTaxGift) {
        // Tax gift mode: allow any cards, but enforce the required count by
        // dropping the oldest selection when we exceed it.
        while (this.selectedIds.size >= taxNeeded) {
          const oldest = this.selectedIds.values().next().value;
          if (oldest === undefined) break;
          this.selectedIds.delete(oldest);
          const oldSprite = this.cardSprites.get(oldest);
          if (oldSprite) {
            oldSprite.setSelected(false);
            this.tweens.add({
              targets: oldSprite,
              y: 0,
              duration: 120,
              ease: 'Quad.easeOut',
            });
          }
        }
      } else if (card.rank !== 13) {
        // Playing-phase rule 1: when selecting a card of a new non-joker
        // rank, drop any previously selected cards of a different non-joker
        // rank. Jokers stack with whatever rank is currently being built.
        const toDeselect: number[] = [];
        for (const otherId of this.selectedIds) {
          const other = this.hand.find((c) => c.id === otherId);
          if (!other) continue;
          if (other.rank !== 13 && other.rank !== card.rank) {
            toDeselect.push(otherId);
          }
        }
        for (const otherId of toDeselect) {
          this.selectedIds.delete(otherId);
          const otherSprite = this.cardSprites.get(otherId);
          if (otherSprite) {
            otherSprite.setSelected(false);
            this.tweens.add({
              targets: otherSprite,
              y: 0,
              duration: 120,
              ease: 'Quad.easeOut',
            });
          }
        }
      }
      this.selectedIds.add(id);
    }

    if (sprite) {
      sprite.setSelected(this.selectedIds.has(id));
      this.tweens.add({
        targets: sprite,
        y: this.selectedIds.has(id) ? -22 : 0,
        duration: 120,
        ease: 'Quad.easeOut',
      });
    }
    this.refreshActionButtons();
  }

  private playSelected(): void {
    const cardIds = [...this.selectedIds];
    if (cardIds.length === 0) return;
    if (this.myPendingTaxCount() > 0) {
      this.room.send(CLIENT_MESSAGES.SUBMIT_TAX, { cardIds });
    } else {
      this.room.send(CLIENT_MESSAGES.PLAY_CARDS, { cardIds });
    }
    this.selectedIds.clear();
  }

  private mountActions(): void {
    const root = document.getElementById('overlay');
    if (!root) return;

    // Top-right toolbar: leave + sound. Casino-style chips.
    // Revolution moved into the action button slot (above the hand) and
    // is only shown during the tax phase when the player holds 2+ jokers.
    const toolbar = document.createElement('div');
    toolbar.style.cssText = `
      position: absolute;
      top: 18px;
      right: 24px;
      display: flex;
      gap: 8px;
      align-items: center;
      pointer-events: auto;
      z-index: 70;
      transform-origin: top right;
      transform: scale(var(--hud-scale, 1));
    `;
    toolbar.innerHTML = `
      <div id="dm-status-chip" title="내 상태 (직위 · 남은 카드)" style="
        display:flex; align-items:center; gap:7px;
        padding: 8px 14px; border-radius: 22px;
        background: linear-gradient(180deg, rgba(40,18,22,0.95), rgba(26,8,10,0.95));
        border: 1px solid rgba(245,200,105,0.4);
        box-shadow: 0 4px 10px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,220,150,0.15);
        font-weight: 700; font-size: 13px; letter-spacing: 0.3px; white-space: nowrap;
        pointer-events: none;
      ">
        <span id="sc-title" style="color:#f5c869;">직위 미정</span>
        <span style="color:rgba(245,200,105,0.4);">·</span>
        <span id="sc-count" style="color:#f4ecd6;">🃏 -</span>
      </div>
      <button id="dm-sound" title="사운드" style="
        width:40px; height:40px; border-radius:50%;
        background: linear-gradient(180deg, rgba(40,18,22,0.95), rgba(26,8,10,0.95));
        border: 1px solid rgba(245,200,105,0.4);
        color: #f5c869; font-size:18px; cursor:pointer;
        box-shadow: 0 4px 10px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,220,150,0.15);
      ">🔊</button>
      <button id="dm-leave" style="
        padding: 9px 18px; border-radius: 22px;
        background: linear-gradient(180deg, rgba(40,18,22,0.95), rgba(26,8,10,0.95));
        border: 1px solid rgba(245,200,105,0.4); color: #f5c869;
        font-weight: 700; font-size: 13px; letter-spacing: 0.5px; cursor: pointer;
        box-shadow: 0 4px 10px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,220,150,0.15);
      ">↩ 나가기</button>
    `;
    root.appendChild(toolbar);
    this.actionPanel = toolbar;
    // The status chip lives inside the toolbar; reuse the myCardPanel slot so
    // existing update / spectator-hide / cleanup logic keeps working.
    this.myCardPanel = toolbar.querySelector<HTMLDivElement>('#dm-status-chip');

    const leaveBtn = toolbar.querySelector<HTMLButtonElement>('#dm-leave')!;
    const soundBtn = toolbar.querySelector<HTMLButtonElement>('#dm-sound')!;
    leaveBtn.addEventListener('click', () => this.room.leave());
    soundBtn.addEventListener('click', () => {
      const nextMuted = !isMuted();
      setMuted(nextMuted);
      soundBtn.textContent = nextMuted ? '🔇' : '🔊';
    });
    this.setupFullscreenButton();

    // Action slot above the hand panel. Holds the giant PASS button during
    // play, or the REVOLUTION button during the tax phase (when eligible).
    const passWrap = document.createElement('div');
    passWrap.id = 'dm-pass-wrap';
    passWrap.style.cssText = `
      position: absolute;
      bottom: ${CARD_HEIGHT + 56}px;
      right: 40px;
      pointer-events: auto;
      z-index: 60;
      display: none;
    `;
    // Stack vertically so the 내기(play) button sits directly ABOVE the PASS
    // button; align-items:stretch makes every button the same width (= the
    // widest, i.e. PASS) so 내기 and PASS render at identical size.
    passWrap.style.cssText +=
      'display: none; flex-direction: column; gap: 12px; align-items: stretch;';
    passWrap.innerHTML = `
      <button id="dm-play" title="선택한 카드를 내기" style="
        padding: 18px 38px;
        border-radius: 18px;
        background: linear-gradient(180deg, #7ed87e 0%, #3a9d4e 100%);
        border: 3px solid #246b30;
        color: #fff;
        font-family: 'Bangers','Bagel Fat One','Black Han Sans',system-ui,sans-serif;
        font-size: 36px;
        font-weight: 900;
        letter-spacing: 2px;
        cursor: pointer;
        transform: rotate(-4deg);
        text-shadow: 3px 4px 0 rgba(0,0,0,0.4), 0 0 10px rgba(126,216,126,0.6);
        box-shadow: 0 8px 0 #246b30, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.35);
        transition: transform 0.08s, box-shadow 0.08s, opacity 0.12s;
        display: none;
      ">내기!</button>
      <button id="dm-pass" style="
        padding: 18px 38px;
        border-radius: 18px;
        background: linear-gradient(180deg, #6db4e0 0%, #2f6ea3 100%);
        border: 3px solid #1a3a5c;
        color: #fff;
        font-family: 'Bangers','Bagel Fat One','Black Han Sans',system-ui,sans-serif;
        font-size: 36px;
        font-weight: 900;
        letter-spacing: 2px;
        cursor: pointer;
        transform: rotate(-4deg);
        text-shadow: 3px 4px 0 rgba(0,0,0,0.4), 0 0 10px rgba(110,180,224,0.6);
        box-shadow: 0 8px 0 #1a3a5c, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.35);
        transition: transform 0.08s, box-shadow 0.08s;
        display: none;
      ">PASS!!</button>
      <button id="dm-tax-gift" title="선택한 카드를 하사 (상납 단계)" style="
        padding: 18px 30px;
        border-radius: 18px;
        background: linear-gradient(180deg, #f5c869 0%, #c89230 100%);
        border: 3px solid #6f4a14;
        color: #2c0b0e;
        font-family: 'Bangers','Bagel Fat One','Black Han Sans',system-ui,sans-serif;
        font-size: 30px;
        font-weight: 900;
        letter-spacing: 2px;
        cursor: pointer;
        transform: rotate(-3deg);
        text-shadow: 2px 3px 0 rgba(255,240,200,0.5), 0 0 10px rgba(245,200,105,0.4);
        box-shadow: 0 8px 0 #6f4a14, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.4);
        transition: transform 0.08s, box-shadow 0.08s;
        display: none;
      ">🎁 하사!</button>
      <button id="dm-revolution" title="혁명 선언 — 상납 취소 · 꼴등이 선 · 역순 진행" style="
        padding: 18px 32px;
        border-radius: 18px;
        background: linear-gradient(180deg, #e84545 0%, #8a1a1a 100%);
        border: 3px solid #5a1010;
        color: #fff;
        font-family: 'Bangers','Bagel Fat One','Black Han Sans',system-ui,sans-serif;
        font-size: 32px;
        font-weight: 900;
        letter-spacing: 3px;
        cursor: pointer;
        transform: rotate(3deg);
        text-shadow: 3px 4px 0 rgba(0,0,0,0.5), 0 0 14px rgba(232,69,69,0.7);
        box-shadow: 0 8px 0 #5a1010, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.3);
        transition: transform 0.08s, box-shadow 0.08s;
        display: none;
      ">⚔ 혁명!</button>
      <button id="dm-decline-rev" title="혁명하지 않고 상납 진행" style="
        padding: 16px 26px;
        border-radius: 18px;
        background: linear-gradient(180deg, #cdb98e 0%, #8a7a52 100%);
        border: 3px solid #5a4a2a;
        color: #2c0b0e;
        font-family: 'Bangers','Bagel Fat One','Black Han Sans',system-ui,sans-serif;
        font-size: 24px;
        font-weight: 900;
        letter-spacing: 2px;
        cursor: pointer;
        transform: rotate(-2deg);
        box-shadow: 0 8px 0 #5a4a2a, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.3);
        transition: transform 0.08s, box-shadow 0.08s;
        display: none;
      ">넘기기</button>
    `;
    root.appendChild(passWrap);
    this.passWrap = passWrap;
    this.passButton = passWrap.querySelector<HTMLButtonElement>('#dm-pass')!;
    this.revolutionButton = passWrap.querySelector<HTMLButtonElement>('#dm-revolution');
    this.declineRevolutionButton =
      passWrap.querySelector<HTMLButtonElement>('#dm-decline-rev');
    this.declineRevolutionButton?.addEventListener('click', () => {
      this.room.send(CLIENT_MESSAGES.DECLINE_REVOLUTION, {});
    });
    this.taxGiftButton = passWrap.querySelector<HTMLButtonElement>('#dm-tax-gift');
    this.taxGiftButton?.addEventListener('click', () => this.playSelected());
    this.taxGiftButton?.addEventListener('mousedown', () => {
      this.taxGiftButton!.style.transform = 'rotate(-3deg) translateY(4px)';
      this.taxGiftButton!.style.boxShadow = '0 4px 0 #6f4a14, 0 6px 12px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.4)';
    });
    this.taxGiftButton?.addEventListener('mouseup', () => {
      this.taxGiftButton!.style.transform = 'rotate(-3deg)';
      this.taxGiftButton!.style.boxShadow = '0 8px 0 #6f4a14, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.4)';
    });
    this.playButton = passWrap.querySelector<HTMLButtonElement>('#dm-play');
    this.playButton?.addEventListener('click', () => {
      if (this.canPlaySelection()) this.playSelected();
      this.refreshActionButtons();
    });
    this.playButton?.addEventListener('mousedown', () => {
      if (this.playButton!.disabled) return;
      this.playButton!.style.transform = 'rotate(-4deg) translateY(4px)';
      this.playButton!.style.boxShadow = '0 4px 0 #246b30, 0 6px 12px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.35)';
    });
    this.playButton?.addEventListener('mouseup', () => {
      this.playButton!.style.transform = 'rotate(-4deg)';
      this.playButton!.style.boxShadow = '0 8px 0 #246b30, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.35)';
    });
    this.passButton.addEventListener('click', () => this.handlePass());
    this.passButton.addEventListener('mousedown', () => {
      this.passButton!.style.transform = 'rotate(-4deg) translateY(4px)';
      this.passButton!.style.boxShadow = '0 4px 0 #1a3a5c, 0 6px 12px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.35)';
    });
    this.passButton.addEventListener('mouseup', () => {
      this.passButton!.style.transform = 'rotate(-4deg)';
      this.passButton!.style.boxShadow = '0 8px 0 #1a3a5c, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.35)';
    });
    this.revolutionButton?.addEventListener('click', () => {
      this.room.send(CLIENT_MESSAGES.CALL_REVOLUTION, {});
    });
    this.revolutionButton?.addEventListener('mousedown', () => {
      this.revolutionButton!.style.transform = 'rotate(3deg) translateY(4px)';
      this.revolutionButton!.style.boxShadow = '0 4px 0 #5a1010, 0 6px 12px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.3)';
    });
    this.revolutionButton?.addEventListener('mouseup', () => {
      this.revolutionButton!.style.transform = 'rotate(3deg)';
      this.revolutionButton!.style.boxShadow = '0 8px 0 #5a1010, 0 12px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.3)';
    });

    // Spacebar: play the current selection if it's a valid play.
    this.spaceKeyHandler = (e: KeyboardEvent): void => {
      if (e.code !== 'Space') return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return;
      }
      if (this.selectedIds.size === 0) return;
      // Only play when the current selection is a valid play (tax gift or
      // matching trick); silently ignore otherwise.
      if (!this.canPlaySelection()) return;
      e.preventDefault();
      this.playSelected();
    };
    window.addEventListener('keydown', this.spaceKeyHandler);

    this.refreshActionButtons();
  }

  private mountChatFeed(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const feed = document.createElement('div');
    feed.style.cssText =
      'position:absolute; right:24px; bottom:90px; width:300px; pointer-events:none; display:flex; flex-direction:column; gap:6px; align-items:flex-end; text-align:right; transform-origin:bottom right; transform:scale(var(--game-scale,1));';
    root.appendChild(feed);
    this.chatFeed = feed;
  }

  private mountChatInput(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const bar = document.createElement('div');
    bar.style.cssText =
      'position:absolute; right:24px; bottom:calc(24px + var(--kb-inset, 0px)); width:300px; pointer-events:auto; display:flex; gap:6px; align-items:center; transition:bottom 0.2s; transform-origin:bottom right; transform:scale(var(--hud-scale,1));';
    bar.innerHTML = `
      <input id="dm-chat-input" placeholder="메시지 (Enter)" enterkeyhint="send" autocomplete="off" autocapitalize="off" style="flex:1; background:rgba(10,7,5,0.55); border:1px solid rgba(212,176,106,0.3); font-size:16px; color:#f4ecd6; border-radius:18px; padding:8px 14px;" />
    `;
    root.appendChild(bar);
    this.chatInputBar = bar;
    const input = bar.querySelector<HTMLInputElement>('#dm-chat-input')!;
    let composing = false;
    input.addEventListener('compositionstart', () => {
      composing = true;
    });
    input.addEventListener('compositionend', () => {
      composing = false;
    });
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      if (composing || e.isComposing || e.keyCode === 229) return;
      e.preventDefault();
      const text = input.value.trim();
      if (!text) return;
      this.room.send(CLIENT_MESSAGES.CHAT, { text });
      input.value = '';
    });
  }

  private mountGameLog(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const panel = document.createElement('div');
    panel.style.cssText =
      'position:absolute; left:24px; top:24px; width:340px; max-height:260px; overflow:hidden; pointer-events:none; display:flex; flex-direction:column; gap:4px; color:#5a3008; font-size:16px; font-weight:600; font-family: "Apple SD Gothic Neo", "Segoe UI", sans-serif; text-shadow: 0 1px 0 rgba(255,255,255,0.6); letter-spacing: 0.2px; background: rgba(255,247,224,0.55); padding: 12px 14px; border-radius: 12px; border: 1px solid rgba(200,155,47,0.45); transform-origin:top left; transform:scale(var(--game-scale,1));';
    root.appendChild(panel);
    this.gameLogPanel = panel;
  }

  private pushGameLog(text: string): void {
    const panel = this.gameLogPanel;
    if (!panel) return;
    const line = document.createElement('div');
    line.textContent = text;
    line.style.cssText =
      'opacity:1; transition: opacity 0.8s ease-out;';
    panel.appendChild(line);
    while (panel.childElementCount > 6) panel.removeChild(panel.firstChild!);
    setTimeout(() => {
      line.style.opacity = '0.45';
    }, 5000);
  }

  private pushChatLine(text: string, color: string): void {
    const feed = this.chatFeed;
    if (!feed) return;
    const line = document.createElement('div');
    line.textContent = text;
    line.style.cssText =
      `color:${color}; font-size:15px; font-weight:600; max-width:300px; word-break:break-word; transition: opacity 0.8s ease-out; text-shadow: 0 1px 0 rgba(255,255,255,0.7); background: rgba(255,247,224,0.85); padding: 4px 10px; border-radius: 14px; border: 1px solid rgba(200,155,47,0.3); box-shadow: 0 2px 4px rgba(58,36,16,0.1);`;
    feed.appendChild(line);
    while (feed.childElementCount > 6) feed.removeChild(feed.firstChild!);
    this.floatingChatLines.push({
      el: line,
      expiresAt: Date.now() + 7000,
    });
  }

  private tickChatFade(): void {
    const now = Date.now();
    const next: FloatingLine[] = [];
    for (const item of this.floatingChatLines) {
      if (now > item.expiresAt + 800) {
        item.el.remove();
      } else if (now > item.expiresAt) {
        item.el.style.opacity = '0';
        next.push(item);
      } else {
        next.push(item);
      }
    }
    this.floatingChatLines = next;
  }

  private cleanup(): void {
    if (this.cleanedUp) return;
    this.cleanedUp = true;
    this.actionPanel?.remove();
    this.passWrap?.remove();
    if (this.spaceKeyHandler) {
      window.removeEventListener('keydown', this.spaceKeyHandler);
      this.spaceKeyHandler = null;
    }
    this.chatFeed?.remove();
    this.chatInputBar?.remove();
    this.gameLogPanel?.remove();
    this.statusBadge?.remove();
    this.trickPanel?.remove();
    this.myCardPanel?.remove();
    this.resultBanner?.remove();
    this.nextGameButton?.remove();
    this.nextGameButton = null;
    this.skipButton?.remove();
    this.skipButton = null;
    if (this.fsChangeHandler) {
      document.removeEventListener('fullscreenchange', this.fsChangeHandler);
      this.fsChangeHandler = null;
    }
    this.fullscreenButton?.remove();
    this.fullscreenButton = null;
    // Leaving the game room — drop out of fullscreen so the lobby/room aren't
    // stuck in the immersive mode the game requested.
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
    this.orientationHint?.remove();
    this.orientationHint = null;
    this.actionPanel = null;
    this.chatFeed = null;
    this.chatInputBar = null;
    this.gameLogPanel = null;
    this.statusBadge = null;
    this.trickPanel = null;
    this.myCardPanel = null;
    this.resultBanner = null;
    this.playButton = null;
    this.passButton = null;
    this.revolutionButton = null;
    for (const b of this.seatBadges.values()) b.destroy();
    this.seatBadges.clear();
    if (this.fadeTickerHandle !== -1) {
      window.clearInterval(this.fadeTickerHandle);
      this.fadeTickerHandle = -1;
    }
    this.scale.off('resize', this.resizeHandler);
    if (this.room?.connection?.isOpen) {
      this.room.leave().catch(() => {});
    }
  }
}
